import { motion } from "framer-motion";
import { Check, X, Shield, Clock, Lock } from "lucide-react";

export default function QuickScanCard({ data }) {
  if (data?.fallback) {
    return (
      <section className="mx-auto max-w-2xl px-4 py-12 text-center" data-testid="incomplete-quick-scan" aria-live="polite">
        <h2 className="text-xl font-semibold text-white">Inspección incompleta</h2>
        <p className="mt-3 text-sm text-zinc-400">No se ha obtenido una puntuación fiable y no mostraremos conclusiones ni ofertas hasta completar la inspección.</p>
      </section>
    );
  }

  const getScoreColor = (s) => {
    if (s >= 80) return "#39FF14";
    if (s >= 50) return "#FFCC00";
    return "#FF3B30";
  };

  const color = getScoreColor(data.score);

  // Select 3 checks: 2 passed + 1 failed
  const passed = data.checks?.filter((c) => c.passed) || [];
  const failed = data.checks?.filter((c) => !c.passed) || [];
  const visibleChecks = [...passed.slice(0, 2), ...failed.slice(0, 1)];
  const hiddenCount = (data.checks?.length || 0) - visibleChecks.length;

  return (
    <motion.section
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="py-12 px-4 bg-[#05050A]"
      data-testid="quick-scan-results"
    >
      <div className="max-w-3xl mx-auto">
        {/* Score */}
        <div className="bg-[#0F0F1A] border border-white/5 rounded-2xl p-8 text-center mb-4">
          <p className="text-xs text-zinc-500 font-mono mb-4">{data.url}</p>
          <div className="flex items-baseline justify-center gap-2 mb-2">
            <span
              className="text-6xl sm:text-7xl font-black"
              style={{ color, fontFamily: "Cabinet Grotesk, sans-serif", textShadow: `0 0 30px ${color}40` }}
              data-testid="quick-score"
            >
              {data.score}
            </span>
            <span className="text-xl text-zinc-500">/100</span>
          </div>

          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 text-sm text-zinc-300">
              <Clock className="w-3.5 h-3.5 text-zinc-400" />
              {data.response_time}s
            </div>
            <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm ${data.is_https ? "bg-[#39FF14]/10 text-[#39FF14]" : "bg-red-500/10 text-red-400"}`}>
              <Shield className="w-3.5 h-3.5" />
              {data.is_https ? "HTTPS" : "No HTTPS"}
            </div>
          </div>

          {/* 3 visible checks */}
          <div className="space-y-2 mb-4">
            {visibleChecks.map((check, i) => (
              <div key={i} className="flex items-center gap-2.5 p-3 rounded-lg bg-white/[0.02] text-left">
                {check.passed ? (
                  <Check className="w-4 h-4 text-[#39FF14] shrink-0" />
                ) : (
                  <X className="w-4 h-4 text-[#FF3B30] shrink-0" />
                )}
                <span className="text-sm text-zinc-300 flex-1">{check.name}</span>
                <span className="text-xs text-zinc-500">{check.detail}</span>
              </div>
            ))}
          </div>

          {/* Locked checks */}
          {hiddenCount > 0 && (
            <div className="relative overflow-hidden rounded-lg mb-6">
              <div className="space-y-2 opacity-20 blur-[2px] pointer-events-none">
                {data.checks?.slice(3, 6).map((check, i) => (
                  <div key={i} className="flex items-center gap-2.5 p-3 rounded-lg bg-white/[0.02]">
                    <div className="w-4 h-4 rounded bg-zinc-700" />
                    <span className="text-sm text-zinc-500">{check.name}</span>
                  </div>
                ))}
              </div>
              <div className="absolute inset-0 flex items-center justify-center bg-[#0F0F1A]/60">
                <div className="flex items-center gap-2 text-sm text-zinc-400">
                  <Lock className="w-4 h-4" />
                  +{hiddenCount} checks bloqueados
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.section>
  );
}
